import {
  EditIcon,
  IconButton,
  Modal,
  TrashIcon,
  type IconButtonSize,
  type IconButtonTone,
} from "@carbon6/c6-frontend-design";
import { type CSSProperties, type ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";

// 헤더 액션 하나의 설정이다. 아이콘/라벨/disabled 를 커스터마이즈할 때 객체로 전달한다.
export interface HatModalHeaderAction {
  // 클릭 핸들러.
  onClick: () => void;
  // 접근성 라벨과 기본 툴팁 텍스트다. 생략하면 액션별 기본값(편집/삭제/닫기)을 쓴다.
  label?: string;
  // 툴팁 문구 override 다. 생략하면 label 을 쓴다.
  tooltip?: string;
  // 아이콘 override 다. 생략하면 액션별 기본 인라인 SVG 를 쓴다.
  icon?: ReactNode;
  // 비활성화.
  disabled?: boolean;
}

// 핸들러 함수(간단)와 설정 객체(커스터마이즈)를 모두 허용한다.
export type HatModalHeaderActionInput = HatModalHeaderAction | (() => void);

export interface HatModalHeaderProps {
  // 좌측 타이틀(평문/인라인 노드)이다. h2 로 렌더되고 Modal 의 accessible name 에 자동으로 연결된다.
  title?: ReactNode;
  // 타이틀 h2 의 id 다. 보통 생략한다(Modal 안에서 자동 생성). 외부에서 직접 참조할 때만 지정한다.
  titleId?: string;
  // 편집 액션이다(연필 아이콘). 값이 있을 때만 렌더한다.
  edit?: HatModalHeaderActionInput;
  // 삭제 액션이다(휴지통 아이콘, danger(빨강) tone). 값이 있을 때만 렌더한다.
  delete?: HatModalHeaderActionInput;
  // 닫기 액션이다(X 아이콘). 값이 있을 때만 렌더한다. c6 `Modal.Header.Close` 가 클릭을 Modal 의
  // onClose 에 자동으로 연결한다(Escape/backdrop 과 같은 경로). 그래서 핸들러는 받지 않고, 표시
  // 여부와 busy 게이트(disabled)만 정한다.
  close?: boolean | { disabled?: boolean };
  // delete 와 close 사이에 들어갈 커스텀 액션 슬롯이다.
  extraActions?: ReactNode;
  // 액션 버튼 공통 size 다. 기본값은 "md" 다.
  size?: IconButtonSize;
  // 헤더와 본문 사이 구분선이다. 생략하면 c6 Modal.Header 기본값(true)이다. 헤더와 본문을 붙이려면 false 를 준다.
  divider?: boolean;
  // 헤더 root 에 적용한다.
  className?: string;
  style?: CSSProperties;
}

type ActionKind = "edit" | "delete";

const ACTION_DEFAULTS: Record<
  ActionKind,
  { label: string; icon: ReactNode; tone: IconButtonTone }
> = {
  edit: { label: "편집", icon: <EditIcon />, tone: "primary" },
  delete: { label: "삭제", icon: <TrashIcon />, tone: "danger" },
};

// 함수형 입력을 설정 객체로 정규화한다. 이후 로직은 객체만 다룬다.
const normalizeAction = (
  input: HatModalHeaderActionInput | undefined,
): HatModalHeaderAction | undefined =>
  input == null ? undefined : typeof input === "function" ? { onClick: input } : input;

const renderAction = (
  kind: ActionKind,
  input: HatModalHeaderActionInput | undefined,
  size: IconButtonSize,
) => {
  const action = normalizeAction(input);
  if (!action) return null;
  const def = ACTION_DEFAULTS[kind];
  const label = action.label ?? def.label;
  // 액션 버튼의 hover 툴팁은 앱 공용 HatTextTooltip 을 쓴다. 화살표·색을 맞추려고 c6 Tooltip 은 쓰지 않는다.
  return (
    <HatTextTooltip key={kind} text={action.tooltip ?? label} placement="bottom">
      <IconButton
        data-c6-modal-action={kind}
        variant="ghost"
        border={false}
        tone={def.tone}
        size={size}
        aria-label={label}
        disabled={action.disabled}
        onClick={action.onClick}
      >
        {action.icon ?? def.icon}
      </IconButton>
    </HatTextTooltip>
  );
};

/**
 * Modal 헤더다. 좌측 타이틀과 우상단 액션 버튼 그룹을 일관되게 구성하는 헬퍼다.
 *
 * c6 `Modal.Header` compound parts(`Start`/`Title`/`End`/`Close`) 위에 앱이 쓰는 prop 기반 헤더를 만든다.
 * - 순서: prop 순서와 관계없이 항상 edit → delete → extraActions → close 로 렌더한다. 값이 있는 액션만 보인다.
 * - 버튼: 아이콘만 있는 ghost `IconButton`(hover 툴팁은 `HatTextTooltip`). delete 는 danger tone 이다.
 *   close 는 `Modal.Header.Close`(X)다. Modal 의 onClose 에 자동으로 연결된다(Escape/backdrop 과 같은 경로).
 *   그래서 핸들러를 받지 않는다.
 * - title 을 주면 `Modal.Header.Title` 로 렌더되고 Modal 의 accessible name 에 자동으로 연결된다.
 */
export function HatModalHeader({
  title,
  titleId,
  edit,
  delete: deleteAction,
  close,
  extraActions,
  size = "md",
  divider,
  className,
  style,
}: HatModalHeaderProps): ReactNode {
  const closeDisabled = typeof close === "object" ? close.disabled : undefined;
  return (
    <Modal.Header divider={divider} className={className} style={style}>
      <Modal.Header.Start>
        {title != null ? <Modal.Header.Title titleId={titleId}>{title}</Modal.Header.Title> : null}
      </Modal.Header.Start>
      <Modal.Header.End>
        {renderAction("edit", edit, size)}
        {renderAction("delete", deleteAction, size)}
        {extraActions}
        {/* 라벨(=툴팁)은 한국어로 준다. c6 기본값은 "Close" 다. 그대로 두면 앱 모달의 X 만
            영어 툴팁을 표시한다. edit·delete 기본 라벨과 같은 규칙이다. */}
        {close ? <Modal.Header.Close label="닫기" disabled={closeDisabled} size={size} /> : null}
      </Modal.Header.End>
    </Modal.Header>
  );
}

HatModalHeader.displayName = "HatModalHeader";
